import { Component, ViewChild } from '@angular/core';
import * as SnmpAlertActions from '@modules/snmp-custom-notifications/store/alert/actions';
import * as SnmpDeviceTypeActions from '@modules/snmp-custom-notifications/store/alert/actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { InputButton, ToastService, WizardComponent, WizardStep, WizardStepComponent } from 'mbs-ui-kit';
import { TypeOfSetting } from 'mbs-ui-shared@models/rmm-custom-notification-model';
import { RmmCustomAlertingService } from 'mbs-ui-shared@services/rmm-custom-alert.service';
import { RmmNotificationService } from 'mbs-ui-shared@services/rmm-notifications.service';
import { BehaviorSubject, debounceTime, switchMap } from 'rxjs';
import { RmmCustomAlertingServiceController } from '../rmm-custom-alerting-module/services/rmm-custom-alerting-controller.service';
import { AlertNotificationService } from './services/alert-notification.service';
import { LoadRmmCustomNotificationWizardService } from './utility/load-rmm-custom-notification-wizard.service';

export enum RMMCustomNotificationsWizardSteps {
  Configure = 'configure',
  ApplyTo = 'apply',
  Notifications = 'notifications',
  Alerts = 'alerts',
  Summary = 'summary'
}

@UntilDestroy()
@Component({
  selector: 'mbs-rmm-custom-notification-wizard',
  templateUrl: './rmm-custom-notification-wizard.component.html'
})
export class RMMCustomNotificationWizardComponent {
  @ViewChild(WizardComponent, { static: false }) wizard: WizardComponent;
  @ViewChild('start', { static: false }) startStep: WizardStepComponent;
  public wizardFinish = false;
  public currentStep: WizardStep;

  public stepOrientation = 'vertical';
  public showApplyToStep = false;
  public wizardCheckChanges = true;
  public showSteps = true;
  public showSaveBtnOnClose = false;
  public enableAlertStep = false;
  public companySettingsID = new BehaviorSubject<string>(null);

  public nextButton: InputButton;
  public saveButton: InputButton;
  public backButton: InputButton;
  public isEditMode = false;
  public wizardTitle = '';
  public nextButtonShouldBeEnabled = false;
  public loading = true;

  public titleSteps: any = Object.values(RMMCustomNotificationsWizardSteps).reduce((acc, id) => {
    acc[id] = this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.stepTitles.' + id);
    return acc;
  }, {});

  constructor(
    private store: Store,
    private i18n: I18NextPipe,
    private alertNotificationService: AlertNotificationService,
    private profileSettings: RmmNotificationService,
    private loadRmmCustomNotificationWizardService: LoadRmmCustomNotificationWizardService,
    private customAlertService: RmmCustomAlertingServiceController,
    private alertService: RmmCustomAlertingService,
    private toast: ToastService
  ) {
    this.store.dispatch(SnmpDeviceTypeActions.loadAlertingInfo());
    this.isEditMode = this.loadRmmCustomNotificationWizardService.editMode;

    this.wizardTitle = this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.' + `${this.isEditMode ? 'editWizardTitle' : 'wizardTitle'}`);

    this.setupWizardButtons('');
    this.loadDefaultSettings();
  }

  finishWizard() {
    this.loading = true;
    this.setupSaveButton(false);
    this.getSaveStream()
      .pipe(
        // clear settings cache
        switchMap(() => this.alertService.sendClearSettingCacheRequest()),
        switchMap(() => this.alertService.sendAgentSettingsCommand(TypeOfSetting.Global, null)),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => {
          this.toast.success(this.getToastCompletionText());
          this.loading = false;
          this.closeWizardOnRedirect();
          this.loadRmmCustomNotificationWizardService.resetWizardData();
        },
        error: (err) => {
          this.loading = false;
          this.setupSaveButton(true);
        }
      });
  }

  getToastCompletionText() {
    if (this.loadRmmCustomNotificationWizardService.profileId)
      return this.i18n.transform('mbs-ui:rmm-custom-alerting.successProfileUpdated');

    if (this.showApplyToStep) return this.i18n.transform('mbs-ui:rmm-custom-alerting.successProfileCreated');

    return this.i18n.transform('mbs-ui:rmm-custom-alerting.successToast', {
      value: this.loadRmmCustomNotificationWizardService.totalCount
    });
  }

  getSaveStream() {
    const payload = this.loadRmmCustomNotificationWizardService.getFullPayload(this.enableAlertStep);
    if (this.loadRmmCustomNotificationWizardService.profileId)
      return this.profileSettings.updateProfileSettings(this.loadRmmCustomNotificationWizardService.profileId, payload);

    return this.showApplyToStep
      ? this.profileSettings.registerProfileSettings(payload)
      : this.alertNotificationService.saveAlertNotification(payload);
  }

  handleStepChange(event: any): void {
    this.setupWizardButtons(event.title);
  }

  setupWizardButtons(stepTitle: string) {
    switch (stepTitle) {
      case this.titleSteps[RMMCustomNotificationsWizardSteps.Summary]:
        this.setupBackButton(true);
        this.setupNextButton(false);
        this.setupSaveButton(true);
        break;
      case this.titleSteps[RMMCustomNotificationsWizardSteps.Notifications]:
      case this.titleSteps[RMMCustomNotificationsWizardSteps.Configure]:
      case this.titleSteps[RMMCustomNotificationsWizardSteps.Alerts]:
      default:
        this.setupBackButton(true);
        this.setupNextButton(this.nextButtonShouldBeEnabled);
        this.setupSaveButton(this.isEditMode);
        break;
    }
  }

  setupBackButton(enable = true) {
    this.backButton = {
      text: this.i18n.transform('shared:buttons.back', { format: 'title' }),
      hide: false,
      disabled: !enable
    };
  }

  setupNextButton(enable = true) {
    this.nextButton = {
      text: this.i18n.transform('shared:buttons.next', { format: 'title' }),
      hide: false,
      disabled: !enable
    };
  }

  setupSaveButton(enable = true) {
    this.saveButton = {
      text: this.isEditMode
        ? this.i18n.transform('shared:buttons.save', { format: 'title' })
        : this.i18n.transform('shared:buttons.apply', { format: 'title' }),
      hide: false,
      loading: false,
      disabled: !enable
    };
  }

  closeWizardOnRedirect() {
    this.wizardFinish = true;
  }

  loadDefaultSettings() {
    this.customAlertService.usedSettings.pipe(debounceTime(300), untilDestroyed(this)).subscribe((settings) => {
      this.store.dispatch(SnmpAlertActions.setSnmpAlertCustomisation({ data: settings?.snmpDevice?.alerts ?? [] }));
      this.loadRmmCustomNotificationWizardService.settingsData.next(cloneDeep(settings));
      this.loadRmmCustomNotificationWizardService.notificationsToSave.next(cloneDeep(settings));
      this.showApplyToStep = this.loadRmmCustomNotificationWizardService.showApplyToStep;
      this.loading = false;
    });
  }

  public isCustom(value: boolean) {
    if (value) this.store.dispatch(SnmpAlertActions.setSnmpAlertCustomisation({ data: [] }));
    if (this.enableAlertStep !== value) this.wizard.resetStepsAfter(this.titleSteps.configure);

    this.enableAlertStep = value;
  }

  public disableNext(disable: boolean) {
    this.setupNextButton(!disable);
    if (disable) {
      this.setupSaveButton(false);
    } else {
      this.setupSaveButton(this.isEditMode);
    }
    this.nextButtonShouldBeEnabled = !disable;
  }
}
